import React, { useContext } from 'react';
import isBoolean from 'lodash/isBoolean';
import { withOptimizely } from '@optimizely/react-sdk';
import { FeatureToggleContext } from 'shell/utils/toggles';

/**
 * Read supplied list of feature toggles from Optimizely service and local storage (FeatureToggleContext)
 * and pass it down to children components via props
 *
 * @param {Boolean} withToggleVariables - read and pass all toggle variables to component
 * @param {Object|Boolean} toggleValue - toggle value can be as boolean as object with variables
 * @returns {Object}
 */
const resolveFeatureToggle = (withToggleVariables, toggleValue) => {
  if (withToggleVariables) {
    return isBoolean(toggleValue) ? { value: toggleValue } : toggleValue;
  }

  return isBoolean(toggleValue) ? toggleValue : toggleValue.value;
};

/**
 * Read supplied list of feature toggles from Optimizely service and local storage (FeatureToggleContext)
 * and pass it down to children components via props
 *
 * @param {Object} toggleMap - feature toggle names
 * @param {Boolean} withToggleVariables - read and pass all toggle variables to component
 * @returns {Function}
 */
export const withFeatureToggles = (toggleMap, withToggleVariables) => (Component) => {
  const TogglesConsumer = (props) => {
    const localFeatureToggles = useContext(FeatureToggleContext);
    const { optimizely, isServerSide, optimizelyReadyTimeout, ...restProps } = props;

    const featureProps = Object.keys(toggleMap).reduce((res, mappedFeatureName) => {
      const featureName = toggleMap[mappedFeatureName];
      const localToggle = localFeatureToggles?.[featureName];

      // if a toggles has value in the local storage we should use it instead of the value from optimizely
      if (typeof localToggle !== 'undefined') {
        // if a component needs to have toggles variables we need to wrap all values by object
        return Object.assign(res, { [mappedFeatureName]: resolveFeatureToggle(withToggleVariables, localToggle) });
      }

      // get a toggle value and variables from optimizely
      const value = optimizely?.isFeatureEnabled(featureName);
      const toggleVariables = optimizely?.getFeatureVariables(featureName);

      return Object.assign(res, { [mappedFeatureName]: withToggleVariables ? { value, ...toggleVariables } : value });
    }, {});

    // pass all features with values to the component via the features prop if withToggleVariables is equal true
    const newProps = Object.assign({}, restProps, withToggleVariables ? { features: featureProps } : featureProps);

    return <Component {...newProps} />;
  };

  TogglesConsumer.displayName = `TogglesConsumer(${Component.displayName || Component.name})`;

  return withOptimizely(TogglesConsumer);
};

export default withFeatureToggles;
